import { Injectable } from '@angular/core';
import {BehaviorSubject} from "rxjs";
import * as Comlink from 'comlink';

import {InitParams, RESULT_SPICE, TfSpiceWorker} from "./tf-spice.worker";
import {
  MODEL_URL,
  SPICE_CHUNK_SIZE,
  SPICE_PREDICT_OUT_LEN,
  SPICE_UNIT
} from "../../defines/pitch.const";


/**
 * 주어진 배열을 주어진 크기로 나눈다.
 *
 * @param inputArray
 * @param chunkSize - 분리할 각 chunk의 크기
 */
function splitFloat32ArrayByFixedSize(inputArray: Float32Array, chunkSize: number): Float32Array[] {
  const result: Float32Array[] = [];
  let startIdx = 0;

  while (startIdx < inputArray.length) {
    let endIdx = startIdx + chunkSize;
    const isLastChunk = endIdx >= inputArray.length;

    // 마지막 청크일 경우, endIdx를 배열의 끝으로 설정
    if (isLastChunk) {
      endIdx = inputArray.length;
    }

    const originalChunk = inputArray.subarray(startIdx, endIdx);

    if (isLastChunk && originalChunk.length < chunkSize) {
      // 마지막 청크이고 크기가 chunkSize보다 작을 때 패딩을 추가
      const paddedChunk = new Float32Array(chunkSize);
      paddedChunk.set(originalChunk);
      result.push(paddedChunk);
    } else {
      // 패딩 없이 청크 추가
      result.push(originalChunk);
    }

    startIdx = endIdx;
  }
  return result;
}


/**
 * SPICE에 해당 길이의 입력을 넣었을 때 반환되는 출력의 길이를 반환한다.
 *
 * @param inputLen
 */
function getPredictOutLen(inputLen: number): number {
  let ret = Math.floor( inputLen / SPICE_CHUNK_SIZE) * SPICE_PREDICT_OUT_LEN;
  const remain = inputLen % SPICE_CHUNK_SIZE;
  if (remain > 0) {
    const delta = Math.ceil(remain / SPICE_UNIT) + 1;
    // console.warn('TfSpiceService::getPredictOutLen() =', ret, remain, delta);
    ret += delta;
  }
  return ret;
}


function mergeArrayBuffers(buffers: ArrayBuffer[]): ArrayBuffer {
  // Calculate the total length for the merged array buffer
  let totalLength = 0;
  for (const buffer of buffers) {
    totalLength += buffer.byteLength;
  }

  // Create a new merged array buffer
  const mergedBuffer = new ArrayBuffer(totalLength);

  // Copy all original array buffers into the new buffer
  let offset = 0;
  const mergedView = new Uint8Array(mergedBuffer);
  for (const buffer of buffers) {
    mergedView.set(new Uint8Array(buffer), offset);
    offset += buffer.byteLength;
  }

  return mergedBuffer;
}

async function loadModelFiles(): Promise<InitParams> {
  const modelJsonResponse = await fetch(MODEL_URL + '/model.json');
  const modelJson = await modelJsonResponse.json();

  const weightFileNames = modelJson.weightsManifest[0].paths;

  const weightArrayBuffers = await Promise.all(weightFileNames.map(async (fileName: string) => {
    const weightResponse = await fetch(MODEL_URL + '/' + fileName);
    return await weightResponse.arrayBuffer();
  }));

  const weightsBuffer = mergeArrayBuffers(weightArrayBuffers);
  return { idx: -1, modelJson, weightsBuffer };
}


@Injectable({
  providedIn: 'root'
})
export class TfSpiceService {
  private numWorkers = 0;
  private workers = new Array<Worker>();
  private workerApis = new Array<Comlink.Remote<TfSpiceWorker>>;

  private inited = new BehaviorSubject<boolean>(false);
  public inited$ = this.inited.asObservable();

  constructor() {
    this.init().catch((err) => {
      console.error('TfSpiceService::init() error =', err);
    });
  }

  private async init() {
    if (typeof Worker !== 'undefined') {

      // webworker의 수를 2~6개로 제한한다.
      // TODO: 아이패드에서 확인이 필요.
      this.numWorkers = Math.max(2, Math.min(6, Math.floor(navigator.hardwareConcurrency / 2)));
      console.warn('TfSpiceService::init() numWorkers =', this.numWorkers, navigator.hardwareConcurrency);

      try {
        const initParams = await loadModelFiles();

        for(let i = 0; i < this.numWorkers; ++i) {
          const worker = new Worker(new URL('./tf-spice.worker', import.meta.url));
          this.workers.push(worker);

          // Comlink 타입 선언
          const workerApi = Comlink.wrap<TfSpiceWorker>(worker);
          this.workerApis.push(workerApi);

          initParams.idx = i;

          // 쓰레드 초기화를 2개 단위로 하기. (너무 많은 쓰레드를 동시에 초기화 하게되면 브라우저가 멈춘다)
          if (i % 2 == 0) {
            await workerApi.init(initParams);
          }
          else {
            workerApi.init(initParams).then();
          }
        }

        console.time('TfSpiceService:: warm-up');
        await this.predict(new Float32Array(SPICE_CHUNK_SIZE * 8));
        console.timeEnd('TfSpiceService:: warm-up');

        this.inited.next(true);

        // console.warn('TfSpiceService::init() success');
      }
      catch (err) {
        console.error('PitchService:: tf.init() error =', err);
      }
    }
    else {
      console.error('TfSpiceService::init() typeof Worker === undefined');
    }
  }

  public async predict(float32Array: Float32Array): Promise<RESULT_SPICE> {
    console.warn('TfSpiceService::predict() float32Array.length =', float32Array.length);
    if (this.workerApis.length === 0) {
      throw new Error('TfSpiceService::predict() this.workerApi === undefined');
    }

    console.time('TfSpiceService::predict()');

    // padding으로 넣은 값을 잘라내기위해서
    const orgOutLen = getPredictOutLen(float32Array.length);

    const chunks = splitFloat32ArrayByFixedSize(float32Array, SPICE_CHUNK_SIZE)
    // console.warn('TfSpiceService::predict() chunks =', chunks);

    // Promise.all을 사용하여 모든 워커 작업이 완료될 때까지 기다립니다.
    try {
      const finalResult: RESULT_SPICE = {
        pitches: [],
        uncertainties: [],
      };

      const chunkQueue = chunks.map((chunk, index) => ({ chunk, index }));
      const workerPromises: Promise<void>[] = [];
      const tempResults: { [key: number]: RESULT_SPICE } = {};

      for (const workerApi of this.workerApis) {
        const promise = (async () => {
          while (chunkQueue.length > 0) {
            const currentEntry = chunkQueue.shift();
            if (currentEntry) {
              const { chunk, index } = currentEntry;
              const result = await workerApi.predict(chunk);
              tempResults[index] = result;
            }
          }
        })();
        workerPromises.push(promise);
      }

      await Promise.all(workerPromises);

      // Sort and merge the results based on the chunk index
      Object.keys(tempResults).sort((a, b) => Number(a) - Number(b)).forEach((key) => {
        finalResult.pitches.push(...tempResults[Number(key)].pitches);
        finalResult.uncertainties.push(...tempResults[Number(key)].uncertainties);
      });

      finalResult.pitches = finalResult.pitches.slice(0, orgOutLen);
      finalResult.uncertainties = finalResult.uncertainties.slice(0, orgOutLen);

      console.warn('TfSpiceService::predict() finalResult =', finalResult);
      console.timeEnd('TfSpiceService::predict()');
      return finalResult;
    } catch (error) {
      // 에러 처리
      console.error("An error occurred:", error);
      console.timeEnd('TfSpiceService::predict()');
      throw error;  // 또는 적절한 에러 객체를 반환할 수 있습니다.
    }
  }
}
